import { useAuthStore } from './authStore.js';

/* ===========================================================================
 * THE MENU FOLLOWS THE ROLE AS IT IS NOW, NOT AS IT WAS AT SIGN-IN.
 *
 * Owner, 2026-10-13: a screen taken away from a role stayed in that person's
 * menu ("menu update as user not admin!"). The server applies a Roles & access
 * change on the next request (`forgetAuthUsers()`), but the console read the
 * permission list once, at sign-in, and every menu row and page gate kept
 * answering from that copy until a reload.
 *
 * So the signed-in user is re-read from `/auth/me` when the person comes back
 * to the tab and every few minutes while it is open. The rows a permission no
 * longer covers drop out of the menu, and a page it no longer covers turns
 * them away, without anyone signing out.
 * ======================================================================== */

/** How often an open, visible tab re-reads its permissions. */
export const LIVE_PERMISSIONS_EVERY_MS = 2 * 60_000;
/** Coming back to the tab re-reads at most this often. */
const FOCUS_MIN_GAP_MS = 15_000;

let started = false;
let last = 0;

function refresh(): void {
  const { status, refreshUser } = useAuthStore.getState();
  if (status !== 'authenticated' || document.visibilityState !== 'visible') return;
  last = Date.now();
  void refreshUser();
}

/** Idempotent; called once from `main.tsx`. Returns a stop for tests. */
export function startLivePermissions(): () => void {
  if (started) return () => undefined;
  started = true;
  const onReturn = (): void => {
    if (Date.now() - last >= FOCUS_MIN_GAP_MS) refresh();
  };
  window.addEventListener('focus', onReturn);
  document.addEventListener('visibilitychange', onReturn);
  const timer = window.setInterval(refresh, LIVE_PERMISSIONS_EVERY_MS);
  return () => {
    started = false;
    last = 0;
    window.removeEventListener('focus', onReturn);
    document.removeEventListener('visibilitychange', onReturn);
    window.clearInterval(timer);
  };
}
